// Running an exercise session: timing, answers, scoring, summary.
import { selectedAvatarStyle, selectedPawn } from "../domain/avatars.js";
import { learnerPresentation } from "../domain/learner.js";
import { copy, t } from "../i18n.js";
import { readableError } from "../lib/api.js";
import { app, escapeHTML, showToast } from "../lib/dom.js";
import { difficultyBandInfo, formatSeconds, topicDisplayInfo } from "../lib/format.js";
import { render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";

export function startExercise(assignmentID, overrideExercise = null) {
  clearExerciseTimer();
  const exercise = overrideExercise || state.exercises.find(item => item.assignment_id === assignmentID);
  if (!exercise) return;
  state.exerciseSession = {
    exercise,
    index: 0,
    correct: 0,
    selected: null,
    wrongAnswers: [],
    incorrectAttempts: 0,
    finished: false,
    earnedXP: 0,
    levelUp: false,
    officialScore: null,
    rankingPosition: null,
    saving: false,
    saveError: null,
    streak: 0,
    lastOutcome: null,
    timeRemaining: exercise.time_limit || null
  };
  startExerciseTimer();
  renderExercise();
}

function clearExerciseTimer() {
  if (state.exerciseTimer) {
    clearInterval(state.exerciseTimer);
    state.exerciseTimer = null;
  }
}

function startExerciseTimer() {
  clearExerciseTimer();
  const session = state.exerciseSession;
  if (!session?.timeRemaining) return;
  state.exerciseTimer = setInterval(() => {
    const current = state.exerciseSession;
    if (!current || current.finished || !current.timeRemaining) return clearExerciseTimer();
    current.timeRemaining -= 1;
    if (current.timeRemaining <= 0) {
      current.timeRemaining = 0;
      current.finished = true;
      current.saving = true;
      clearExerciseTimer();
      renderSummary();
      saveExerciseResult();
    } else {
      const timer = document.querySelector("#exercise-timer");
      const timerBar = document.querySelector("#timer-progress span");
      if (timer) timer.textContent = formatSeconds(current.timeRemaining);
      if (timerBar) timerBar.style.width = `${Math.max(0, current.timeRemaining / current.exercise.time_limit * 100)}%`;
    }
  }, 1000);
}

function answerButtons(session, question, className = "answer") {
  return question.answers.map((answer, index) => {
    let buttonClass = className;
    if (session.selected) {
      if (answer === question.correct_answer) buttonClass += " correct";
    } else if (session.wrongAnswers.includes(answer)) {
      buttonClass += " wrong";
    }
    const disabled = session.selected || session.wrongAnswers.includes(answer);
    return `<button class="${buttonClass} mission-answer" data-answer="${escapeHTML(answer)}" ${disabled ? "disabled" : ""}><span class="answer-letter">${String.fromCharCode(65 + index)}</span><strong>${escapeHTML(answer)}</strong><span class="answer-state" aria-hidden="true">${session.selected && answer === question.correct_answer ? "✓" : session.wrongAnswers.includes(answer) ? "×" : "›"}</span></button>`;
  }).join("");
}

function applyExerciseAnswer(session, question, answer) {
  if (answer === question.correct_answer) {
    session.selected = answer;
    session.correct += 1;
    session.streak += 1;
    session.lastOutcome = "correct";
    return false;
  }
  session.incorrectAttempts += 1;
  session.wrongAnswers.push(answer);
  session.streak = 0;
  session.lastOutcome = "wrong";
  return false;
}

function bindExercisePlayControls(session, question) {
  document.querySelectorAll("[data-answer]").forEach(button => {
    button.addEventListener("click", () => {
      const ended = applyExerciseAnswer(session, question, button.dataset.answer);
      if (!ended) renderExercise();
    });
  });
  document.querySelector("#leave-exercise")?.addEventListener("click", () => {
    clearExerciseTimer();
    state.exerciseSession = null;
    state.view = "exercises";
    render();
  });
  document.querySelector("#show-hint")?.addEventListener("click", () => {
    document.querySelector("#hint-card").innerHTML = `<div class="card feedback">${escapeHTML(question.hint)}</div>`;
  });
  document.querySelector("#next-question")?.addEventListener("click", async () => {
    if (session.index < session.exercise.questions.length - 1) {
      session.index += 1;
      session.selected = null;
      session.wrongAnswers = [];
      session.lastOutcome = null;
      renderExercise();
    } else {
      session.finished = true;
      session.saving = true;
      renderSummary();
      await saveExerciseResult();
    }
  });
}

export function renderExercise() {
  const session = state.exerciseSession;
  if (!session) return render();
  if (session.finished) return renderSummary();
  const question = session.exercise.questions[session.index];
  const total = session.exercise.questions.length;
  const percent = ((session.index + (session.selected ? 1 : 0)) / total) * 100;
  const successLabel = t("nice");
  const retryLabel = t("tryAgain");
  const answers = answerButtons(session, question);
  const presentation = learnerPresentation();
  const pawn = selectedPawn();
  const difficulty = difficultyBandInfo(session.exercise.difficulty_level);
  const topic = topicDisplayInfo(session.exercise.topic);
  const guideScene = session.selected
    ? "/brainmath-scenes/character-family-celebration.webp?v=20260725-cutouts"
    : session.wrongAnswers.length
      ? "/brainmath-scenes/character-family-support.webp?v=20260725-cutouts"
      : selectedAvatarStyle() === "female"
        ? "/brainmath-scenes/character-girl.webp?v=20260725-cutouts"
        : "/brainmath-scenes/character-boy.webp?v=20260725-cutouts";
  const guideSceneType = session.selected || session.wrongAnswers.length ? "guide-family" : "guide-learner";
  const guideAlt = session.selected
    ? (state.language === "it" ? "La famiglia BrainMath festeggia la risposta corretta" : "The BrainMath family celebrates the correct answer")
    : session.wrongAnswers.length
      ? (state.language === "it" ? "La famiglia BrainMath incoraggia a riprovare" : "The BrainMath family encourages another try")
      : (state.language === "it" ? "Il personaggio BrainMath riflette sulla domanda" : "The BrainMath character thinks about the question");
  app.innerHTML = `<div class="mission-play-shell student-${presentation.mode}">
    <div class="mission-play-sky" aria-hidden="true"><i></i><i></i><i></i><span>✦</span><span>★</span></div>
    <main class="mission-play-page">
      <header class="mission-command-bar">
        <button class="mission-exit-button" id="leave-exercise" aria-label="${state.language === "it" ? "Esci dalla missione" : "Leave mission"}">← <span>${state.language === "it" ? "Esci" : "Exit"}</span></button>
        <div class="mission-identity"><small>${state.language === "it" ? "Missione in corso" : "Mission in progress"}</small><strong>${escapeHTML(session.exercise.title || topic.title)}</strong></div>
        <div class="mission-clock ${session.timeRemaining !== null ? "timed" : ""}">${session.timeRemaining !== null ? `⏱ <strong id="exercise-timer">${formatSeconds(session.timeRemaining)}</strong>` : `<strong>${session.index + 1}/${total}</strong><small>${t("questions")}</small>`}</div>
      </header>
      <section class="mission-progress-panel">
        <div class="mission-progress-copy"><span>${difficulty.icon} ${escapeHTML(difficulty.label)}</span><strong>${state.language === "it" ? `Domanda ${session.index + 1} di ${total}` : `Question ${session.index + 1} of ${total}`}</strong><span>🔥 ${session.streak} ${state.language === "it" ? "serie" : "streak"}</span></div>
        <div class="mission-progress-track"><span style="width:${percent}%"></span><b style="left:${Math.max(3, Math.min(97, percent))}%">${pawn.emoji}</b></div>
        ${session.timeRemaining !== null ? `<div class="mission-timer-track" id="timer-progress"><span style="width:${Math.max(0, session.timeRemaining / session.exercise.time_limit * 100)}%"></span></div>` : ""}
      </section>
      <section class="mission-question-layout">
        <aside class="mission-guide-card ${session.selected ? "guide-success" : session.wrongAnswers.length ? "guide-retry" : "guide-focus"}">
          <div class="mission-guide-scene character-cutout-stage ${guideSceneType}"><img src="${guideScene}" alt="${escapeHTML(guideAlt)}" decoding="async"><i>✦</i><i>★</i></div>
          <small>${escapeHTML(topic.macro || (state.language === "it" ? "Allenamento" : "Practice"))}</small>
          <h2>${escapeHTML(topic.title)}</h2>
          <p>${state.language === "it" ? "Leggi con calma, scegli la risposta e conquista la prossima stella." : "Take your time, choose the answer and win the next star."}</p>
        </aside>
        <section class="mission-question-card">
          <div class="mission-question-heading"><span class="exercise-symbol">?</span><div><small>${t("question")} ${session.index + 1}</small><strong>${state.language === "it" ? "Qual è la risposta giusta?" : "Which answer is correct?"}</strong></div></div>
          <h1>${escapeHTML(question.prompt)}</h1>
          <div class="answers mission-answers">${answers}</div>
          ${session.selected
            ? `<div class="feedback mission-feedback correct-feedback"><span>🌟</span><div><strong>${escapeHTML(successLabel)}</strong><p>${escapeHTML(question.explanation)}</p></div></div>`
            : session.wrongAnswers.length
              ? `<div class="feedback mission-feedback wrong-feedback"><span>💪</span><div><strong>${escapeHTML(retryLabel)}</strong><p>${state.language === "it" ? "Puoi correggere la risposta. Ogni tentativo riduce leggermente l’esperienza ottenuta." : "You can correct your answer. Each attempt slightly reduces the experience earned."}</p></div></div>`
              : ""}
        </section>
      </section>
      <div id="hint-card" class="mission-hint-slot"></div>
      <div class="mission-action-dock">
        <span>${session.selected ? (state.language === "it" ? "Ottimo lavoro! Sei pronto a continuare." : "Great work! You are ready to continue.") : (state.language === "it" ? "Hai bisogno di una mano? Usa il suggerimento." : "Need a hand? Use the hint.")}</span>
        ${session.selected
          ? `<button class="primary mission-next-button" id="next-question">${session.index === total - 1 ? `🏆 ${t("summary")}` : `${t("next")} →`}</button>`
          : `<button class="secondary mission-hint-button" id="show-hint">💡 ${t("hint")}</button>`}
      </div>
    </main>
  </div>`;
  bindExercisePlayControls(session, question);
}

async function saveExerciseResult() {
  const session = state.exerciseSession;
  const previousLevel = Number(state.profile.level || 0);
  if (!session.exercise.assignment_id) {
    session.exercise.activity_key ||= crypto.randomUUID();
    const { data, error } = await supabase.rpc("record_learning_challenge", {
      p_child_profile_id: state.profile.id,
      p_activity_key: session.exercise.activity_key,
      p_title: session.exercise.title,
      p_topic: session.exercise.topic,
      p_difficulty_level: Number(session.exercise.difficulty_level || 1),
      p_correct_answers: session.correct,
      p_total_questions: session.exercise.questions.length,
      p_incorrect_attempts: session.incorrectAttempts
    });
    session.saving = false;
    if (error || !data?.[0]) {
      session.saveError = readableError(error, state.language === "it" ? "Il risultato non è stato salvato. Riprova." : "The result was not saved. Please retry.");
      showToast(session.saveError);
      renderSummary();
      return;
    }
    session.saveError = null;
    const savedProgress = data[0];
    session.earnedXP = Number(savedProgress.earned_experience || 0);
    state.profile.level = Number(savedProgress.level || 0);
    state.profile.experience = Number(savedProgress.experience || 0);
    state.profile.questions_solved = Number(savedProgress.questions_solved || 0);
    session.levelUp = state.profile.level > previousLevel;
    const profileIndex = state.profiles.findIndex(profile => profile.id === state.profile.id);
    if (profileIndex >= 0) state.profiles[profileIndex] = { ...state.profile };
    renderSummary();
    return;
  }
  const { data, error } = await supabase.rpc("complete_exercise_assignment", {
    selected_assignment: session.exercise.assignment_id,
    correct_answers: session.correct,
    total_questions: session.exercise.questions.length,
    incorrect_attempts: session.incorrectAttempts
  });
  session.saving = false;
  if (error) {
    session.saveError = readableError(error, state.language === "it" ? "Il risultato non è stato salvato. Riprova." : "The result was not saved. Please retry.");
    showToast(session.saveError);
    renderSummary();
    return;
  }
  session.saveError = null;
  session.earnedXP = Number(data || 0);
  const { data: savedProfile, error: profileError } = await supabase
    .from("child_profiles")
    .select("level, experience, questions_solved")
    .eq("id", state.profile.id)
    .single();
  if (profileError) {
    showToast(readableError(profileError, state.language === "it" ? "Risultato salvato; aggiorna la pagina per vedere i progressi." : "Result saved; refresh to view progress."));
  } else {
    state.profile.level = Number(savedProfile.level || 0);
    state.profile.experience = Number(savedProfile.experience || 0);
    state.profile.questions_solved = Number(savedProfile.questions_solved || 0);
  }
  session.levelUp = state.profile.level > previousLevel;
  const profileIndex = state.profiles.findIndex(profile => profile.id === state.profile.id);
  if (profileIndex >= 0) state.profiles[profileIndex] = { ...state.profile };
  state.exercises = state.exercises.filter(item => item.assignment_id !== session.exercise.assignment_id);
  renderSummary();
}

function renderSummary() {
  const session = state.exerciseSession;
  const total = Math.max(1, session.exercise.questions.length);
  const correctAnswers = Math.max(0, Math.min(total, Number(session.correct) || 0));
  const incorrectAttempts = Math.max(0, Number(session.incorrectAttempts) || 0);
  const baseAccuracy = Math.round((correctAnswers / total) * 100);
  const mistakePenalty = incorrectAttempts * 5;
  const score = Math.max(0, Math.min(100, baseAccuracy - mistakePenalty));
  const resultTone = score >= 90 ? "excellent" : score >= 70 ? "good" : score >= 50 ? "growing" : "retry";
  const resultStars = score >= 90 ? 3 : score >= 70 ? 2 : score >= 50 ? 1 : 0;
  const presentation = learnerPresentation();
  const resultMessage = score >= 90
    ? (state.language === "it" ? "Missione straordinaria!" : "Outstanding mission!")
    : score >= 70
      ? (state.language === "it" ? "Missione completata!" : "Mission complete!")
      : (state.language === "it" ? "Ogni prova ti rende più forte!" : "Every try makes you stronger!");
  app.innerHTML = `<div class="mission-play-shell mission-summary-shell student-${presentation.mode}"><div class="summary-confetti" aria-hidden="true">✦<i>★</i><i>●</i><i>✦</i><i>★</i></div><main class="mission-play-page summary-page">
    <section class="mission-result-card xp-summary">
      ${!session.saving && session.earnedXP ? `<div class="xp-stars" aria-hidden="true">${Array.from({ length: 8 }, (_, index) => `<span style="--i:${index}">★</span>`).join("")}</div>` : ""}
      ${session.levelUp ? `<div class="level-up-celebration"><span>🎉</span><h2>${state.language === "it" ? "Nuovo livello!" : "Level up!"}</h2><p>${state.language === "it" ? `Sei arrivato al livello ${state.profile.level}. Il tuo cervello ha fatto un salto buffissimo!` : `You reached level ${state.profile.level}. Your brain just made a very silly leap!`}</p></div>` : ""}
      <div class="result-celebration"><div class="result-character-scene character-cutout-stage"><img src="/brainmath-scenes/character-family-celebration.webp?v=20260725-cutouts" alt="${state.language === "it" ? "La famiglia BrainMath festeggia il risultato" : "The BrainMath family celebrates the result"}"><i>🏆</i></div><div><span class="eyebrow">${t("summary")}</span><h1>${escapeHTML(resultMessage)}</h1><p>${state.language === "it" ? "Hai raggiunto il traguardo. Ecco le stelle conquistate in questa avventura." : "You reached the finish line. Here are the stars earned in this adventure."}</p></div></div>
      <div class="result-score-zone">
        <div class="result-score-overview">
          <div class="score-ring result-${resultTone}" style="--score:${score}%" role="img" aria-label="${escapeHTML(`${t("score")}: ${score}%`)}">
            <div class="score-ring-center">
              <span class="score-stars" aria-hidden="true">${Array.from({ length: 3 }, (_, index) => index < resultStars ? "★" : "☆").join("")}</span>
              <strong>${score}%</strong>
              <small>${t("score")}</small>
            </div>
          </div>
          <p>${state.language === "it" ? "Il risultato considera le risposte corrette e i tentativi usati per correggersi." : "The result reflects correct answers and the attempts used to correct mistakes."}</p>
        </div>
        <div class="result-reward-grid">
        <div class="result-reward correct"><span>✅</span><div><strong>${correctAnswers}/${total}</strong><small>${t("correct")}</small></div></div>
        <div class="result-reward attempts"><span>🧠</span><div><strong>${incorrectAttempts}</strong><small>${state.language === "it" ? "Tentativi da correggere" : "Corrected attempts"}</small></div></div>
        <div class="result-reward xp"><span>⭐</span><div><strong>${session.saving ? "…" : `+${session.earnedXP}`}</strong><small>${t("xp")}</small></div></div>
      </div>
      </div>
      ${session.saveError ? `<div class="feedback mission-feedback wrong-feedback"><span>☁️</span><div><strong>${state.language === "it" ? "Risultato non ancora sincronizzato" : "Result not synced yet"}</strong><p>${escapeHTML(session.saveError)}</p></div></div><button class="primary result-home-button" id="summary-retry">↻ ${state.language === "it" ? "Riprova il salvataggio" : "Retry saving"}</button>` : `<button class="primary result-home-button" id="summary-close" ${session.saving ? "disabled" : ""}>🗺️ ${state.language === "it" ? "Torna all’avventura" : "Back to the adventure"}</button>`}
    </section>
  </main></div>`;
  document.querySelector("#summary-close")?.addEventListener("click", () => {
    clearExerciseTimer();
    state.exerciseSession = null;
    state.view = "home";
    render();
  });
  document.querySelector("#summary-retry")?.addEventListener("click", async () => {
    session.saveError = null;
    session.saving = true;
    renderSummary();
    await saveExerciseResult();
  });
}
